import { useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import ConfirmDelete from "@/components/ConfirmDelete";
import { euro } from "@/lib/api";
import { ArrowUp, ArrowDown, Search, Pencil, Trash2, FolderOpen, Filter } from "lucide-react";

const BASE_COLS = [
  { key: "codice", label: "ODA", type: "text", w: "w-44" },
  { key: "id_cliente", label: "ID Cliente", type: "text", w: "w-36" },
  { key: "tipo", label: "Tipo", type: "text", w: "w-36" },
  { key: "nome", label: "Nome", type: "text", w: "min-w-[180px]" },
  { key: "comune", label: "Comune", type: "text", w: "w-40" },
  { key: "indirizzo", label: "Indirizzo", type: "text", w: "w-56" },
];
const NUM_COLS = [
  { key: "_rend", label: "Rendicontato", type: "num", align: "right", w: "w-32" },
  { key: "_ric", label: "Riconosciuto (BEF)", type: "num", align: "right", w: "w-32" },
  { key: "_delta", label: "Delta", type: "num", align: "right", w: "w-28" },
  { key: "_rit", label: "Ritenute a garanzia", type: "num", align: "right", w: "w-32" },
  { key: "_rec", label: "Ritenute recuperate", type: "num", align: "right", w: "w-32" },
];

const EMPTY = "\u0000__empty__";
const cellRaw = (row, col) => {
  if (col.type === "num") return String(Math.round((row[col.key] || 0) * 100) / 100);
  if (col.type === "count") return String(row[col.key] || 0);
  const v = (row[col.key] || "").toString();
  return v === "" ? EMPTY : v;
};
const cellDisplay = (raw, col) => {
  if (raw === EMPTY) return "(vuoti)";
  if (col.type === "count") return raw;
  return col.type === "num" ? euro(parseFloat(raw)) : raw;
};

function ColumnFilter({ col, values, selected, onChange }) {
  const [q, setQ] = useState("");
  const active = selected && selected.size < values.length;
  const shown = values.filter((v) => cellDisplay(v, col).toLowerCase().includes(q.trim().toLowerCase()));
  const isChecked = (raw) => !selected || selected.has(raw);
  const commit = (base) => onChange(base.size >= values.length ? null : base);
  const toggle = (raw) => {
    const base = selected ? new Set(selected) : new Set(values);
    if (base.has(raw)) base.delete(raw); else base.add(raw);
    commit(base);
  };
  const allShownChecked = shown.length > 0 && shown.every((v) => isChecked(v));
  const toggleAllShown = () => {
    const base = selected ? new Set(selected) : new Set(values);
    if (allShownChecked) shown.forEach((v) => base.delete(v));
    else shown.forEach((v) => base.add(v));
    commit(base);
  };
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button data-testid={`sub-filter-${col.key}`} onClick={(e) => e.stopPropagation()}
          className={`ml-1 rounded p-0.5 hover:bg-slate-300/60 ${active ? "text-blue-600" : "text-slate-400"}`}>
          <Filter className={`h-3 w-3 ${active ? "fill-blue-600" : ""}`} />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-60 p-2" onClick={(e) => e.stopPropagation()}>
        <div className="relative mb-2">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
          <Input data-testid={`sub-filter-search-${col.key}`} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca…" className="pl-7 h-8 text-xs" />
        </div>
        <div className="flex items-center gap-1.5 px-1 py-1 border-b border-slate-100 mb-1">
          <button data-testid={`sub-filter-all-${col.key}`} onClick={() => onChange(null)}
            className="flex-1 text-[11px] font-medium text-blue-700 hover:bg-blue-50 rounded px-1.5 py-1">Seleziona tutto</button>
          <button data-testid={`sub-filter-none-${col.key}`} onClick={() => onChange(new Set())}
            className="flex-1 text-[11px] font-medium text-slate-500 hover:bg-slate-100 rounded px-1.5 py-1">Deseleziona tutto</button>
        </div>
        <div className="max-h-52 overflow-auto space-y-0.5">
          {shown.length === 0 ? <p className="text-xs text-slate-400 px-1 py-2">Nessun valore</p> : shown.map((raw) => (
            <label key={raw} className="flex items-center gap-2 px-1 py-1 rounded hover:bg-slate-50 cursor-pointer">
              <Checkbox checked={isChecked(raw)} onCheckedChange={() => toggle(raw)} data-testid={`sub-filter-opt-${col.key}`} />
              <span className="text-xs text-slate-700 truncate">{cellDisplay(raw, col)}</span>
            </label>
          ))}
        </div>
        {active && (
          <Button variant="ghost" size="sm" data-testid={`sub-filter-clear-${col.key}`} onClick={() => onChange(null)} className="w-full mt-1 h-7 text-xs text-blue-700">Rimuovi filtro</Button>
        )}
      </PopoverContent>
    </Popover>
  );
}

export default function SottocommesseTable({ subs, mode = "commesse", riepilogo = {}, fattureCount = null, onOpen, onEdit, onDelete, isAdmin = false, currentUserId = null }) {
  const isContab = mode === "contabilita";
  const showFatture = !!fattureCount;
  const showActions = !isContab && (isAdmin || (subs || []).some((s) => s.created_by && s.created_by === currentUserId));
  const [sort, setSort] = useState({ key: "codice", dir: "asc" });
  const [filters, setFilters] = useState({}); // { key: Set(raw) | null }

  const fattCol = { key: "_fatture", label: "N° Fatture", type: "count", align: "right", w: "w-24" };
  const baseCols = showFatture ? [...BASE_COLS, fattCol] : BASE_COLS;
  const cols = isContab ? [...baseCols, ...NUM_COLS] : baseCols;

  const mapped = useMemo(() => subs.map((s) => {
    const rp = riepilogo[s.id] || {};
    const rend = rp.rendicontato ?? s.totale ?? 0;
    const ric = rp.riconosciuto || 0;
    return { ...s, _rend: rend, _ric: ric, _delta: ric - rend, _rit: rp.ritenuta || 0, _rec: rp.recuperato || 0, _fatture: (fattureCount && fattureCount[s.id]) || 0 };
  }), [subs, riepilogo, fattureCount]);

  const distinct = useMemo(() => {
    const d = {};
    cols.forEach((c) => { d[c.key] = Array.from(new Set(mapped.map((r) => cellRaw(r, c)))).sort((a, b) => (c.type === "num" || c.type === "count") ? parseFloat(a) - parseFloat(b) : String(a).localeCompare(String(b), "it", { numeric: true })); });
    return d;
  }, [mapped, isContab]); // eslint-disable-line

  const rows = useMemo(() => {
    let r = mapped.filter((row) => cols.every((c) => {
      const sel = filters[c.key];
      return !sel || sel.has(cellRaw(row, c));
    }));
    const { key, dir } = sort;
    const col = cols.find((c) => c.key === key) || BASE_COLS[0];
    const mul = dir === "asc" ? 1 : -1;
    r = [...r].sort((a, b) => {
      if (col.type === "num" || col.type === "count") return ((a[key] || 0) - (b[key] || 0)) * mul;
      return String(a[key] || "").localeCompare(String(b[key] || ""), "it", { numeric: true, sensitivity: "base" }) * mul;
    });
    return r;
  }, [mapped, filters, sort, isContab]); // eslint-disable-line

  const toggleSort = (key) => setSort((p) => (p.key === key ? { key, dir: p.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));

  const totals = useMemo(() => {
    if (!isContab) return null;
    return rows.reduce((t, s) => ({ rend: t.rend + s._rend, ric: t.ric + s._ric, delta: t.delta + s._delta, rit: t.rit + s._rit, rec: t.rec + s._rec }), { rend: 0, ric: 0, delta: 0, rit: 0, rec: 0 });
  }, [rows, isContab]);

  return (
    <div data-testid="sottocommesse-table">
      <div className="border border-slate-200 rounded-md overflow-auto max-h-[480px]">
        <table className="w-full text-sm border-collapse">
          <thead className="sticky top-0 z-10">
            <tr className="bg-slate-100">
              {cols.map((c) => (
                <th key={c.key} data-testid={`sub-th-${c.key}`}
                  className={`${c.w || ""} px-3 py-2.5 text-[10px] font-semibold uppercase tracking-widest text-slate-500 whitespace-nowrap border-b border-slate-200 ${c.align === "right" ? "text-right" : "text-left"}`}>
                  <span className={`inline-flex items-center gap-0.5 ${c.align === "right" ? "flex-row-reverse" : ""}`}>
                    <button onClick={() => toggleSort(c.key)} className="inline-flex items-center gap-1 hover:text-slate-800 select-none">
                      {c.label}
                      {sort.key === c.key && (sort.dir === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />)}
                    </button>
                    <ColumnFilter col={c} values={distinct[c.key] || []} selected={filters[c.key]} onChange={(set) => setFilters((f) => ({ ...f, [c.key]: set }))} />
                  </span>
                </th>
              ))}
              {!isContab && showActions && <th className="w-24 px-3 py-2.5 border-b border-slate-200" />}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={cols.length + (!isContab && showActions ? 1 : 0)} className="px-3 py-8 text-center text-slate-400">Nessuna sottocommessa.</td></tr>
            ) : rows.map((s) => (
              <tr key={s.id} data-testid={`sub-row-${s.id}`}
                onClick={() => onOpen && onOpen(s)}
                className="border-b border-slate-100 last:border-0 hover:bg-blue-50/50 cursor-pointer">
                <td className="px-3 py-2.5 whitespace-nowrap">
                  <span className="inline-flex items-center gap-1.5 font-semibold text-blue-700"><FolderOpen className="h-3.5 w-3.5 text-blue-500" />{s.codice}</span>
                </td>
                <td className="px-3 py-2.5 text-slate-600" data-testid={`sub-idcliente-${s.id}`}>{s.id_cliente || <span className="text-slate-300">—</span>}</td>
                <td className="px-3 py-2.5 text-slate-600" data-testid={`sub-tipo-${s.id}`}>{s.tipo || <span className="text-slate-300">—</span>}</td>
                <td className="px-3 py-2.5 text-slate-700" data-testid={`sub-nome-${s.id}`}>{s.nome}</td>
                <td className="px-3 py-2.5 text-slate-600" data-testid={`sub-comune-${s.id}`}>{s.comune || <span className="text-slate-300">—</span>}</td>
                <td className="px-3 py-2.5 text-slate-600" data-testid={`sub-indirizzo-${s.id}`}>{s.indirizzo || <span className="text-slate-300">—</span>}</td>
                {showFatture && (
                  <td className="px-3 py-2.5 text-right tabular-nums" data-testid={`sub-fatture-${s.id}`}>
                    {s._fatture > 0 ? <span className="inline-flex items-center justify-center min-w-[1.5rem] px-1.5 rounded-full bg-blue-50 text-blue-700 font-semibold">{s._fatture}</span> : <span className="text-slate-300">0</span>}
                  </td>
                )}
                {isContab && <>
                  <td className="px-3 py-2.5 text-right tabular-nums font-semibold text-blue-800" data-testid={`riepilogo-rend-${s.id}`}>{euro(s._rend)}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums font-semibold text-emerald-700" data-testid={`riepilogo-ric-${s.id}`}>{euro(s._ric)}</td>
                  <td className={`px-3 py-2.5 text-right tabular-nums font-semibold ${s._delta < 0 ? "text-rose-600" : "text-emerald-600"}`} data-testid={`riepilogo-delta-${s.id}`}>{s._delta > 0 ? "+" : ""}{euro(s._delta)}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums font-semibold text-amber-700" data-testid={`riepilogo-ritenuta-${s.id}`}>{euro(s._rit)}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums font-semibold text-emerald-600" data-testid={`riepilogo-recuperato-${s.id}`}>{s._rec ? euro(s._rec) : <span className="text-slate-300">—</span>}</td>
                </>}
                {!isContab && showActions && (
                  <td className="px-3 py-2 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                    {(isAdmin || (s.created_by && s.created_by === currentUserId)) && (
                      <Button variant="ghost" size="icon" data-testid={`edit-sub-${s.id}`} onClick={() => onEdit && onEdit(s)} className="h-8 w-8 text-slate-500 hover:text-blue-700"><Pencil className="h-4 w-4" /></Button>
                    )}
                    {isAdmin && (
                      <ConfirmDelete title="Eliminare sottocommessa?" description="Verranno eliminate anche le contabilità e le righe collegate." actionTestid={`confirm-del-sub-${s.id}`} onConfirm={() => onDelete && onDelete(s.id)}>
                        <Button variant="ghost" size="icon" data-testid={`del-sub-${s.id}`} className="h-8 w-8 text-slate-500 hover:text-rose-600"><Trash2 className="h-4 w-4" /></Button>
                      </ConfirmDelete>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
          {isContab && rows.length > 0 && (
            <tfoot className="sticky bottom-0">
              <tr className="bg-slate-50 font-semibold border-t border-slate-200">
                <td className="px-3 py-2.5 text-slate-600 uppercase text-[10px] tracking-widest" colSpan={6}>Totale commessa madre</td>
                <td className="px-3 py-2.5 text-right tabular-nums text-blue-800" data-testid="riepilogo-totale">{euro(totals.rend)}</td>
                <td className="px-3 py-2.5 text-right tabular-nums text-emerald-700" data-testid="riepilogo-totale-ric">{euro(totals.ric)}</td>
                <td className={`px-3 py-2.5 text-right tabular-nums ${totals.delta < 0 ? "text-rose-600" : "text-emerald-600"}`} data-testid="riepilogo-totale-delta">{totals.delta > 0 ? "+" : ""}{euro(totals.delta)}</td>
                <td className="px-3 py-2.5 text-right tabular-nums text-amber-700" data-testid="riepilogo-totale-ritenuta">{euro(totals.rit)}</td>
                <td className="px-3 py-2.5 text-right tabular-nums text-emerald-600" data-testid="riepilogo-totale-recuperato">{euro(totals.rec)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}
